/**
 * @file This file contains static content for the activity.
 */
import {
  feetToMeters,
  degreesToRadians,
  sexagesimalToDecimal
} from "@/helpers/surveyingUtils";

/**
 * The acceptable error for each measurement step
 * Units are meters, since internal calculations are in meters
 * @typedef {Object} ErrorMeasurementTolerance
 * @property {number} MP - The acceptable error for the MP measurement in meters
 * @property {number} CT2 - The acceptable error for the CT2 measurement in meters
 */
const ErrorMeasurementTolerance = {
  // acceptable error is 1 inch
  MP: feetToMeters(1 / 12),
  // acceptable error is 2 inches
  CT2: feetToMeters(2 / 12)
};

/**
 * The ideal maximum acceptable error for each measurement step
 * This is only used for display purposes, and is not used in any calculations
 * Units are ft, since the lab handout is in feet
 * @typedef {Object} IdealMeasurementTolerance
 * @property {number} MP - The ideal error for the MP measurement in feet
 * @property {number} CT2 - The ideal error for the CT2 measurement in feet
 */
const IdealMeasurementTolerance = {
  MP: 0.05,
  CT2: 0.11
};

/**
 * Constants for the curve, in feet, as displayed in the lab handout
 * Values are taken from the lab handout, located in the resources folder
 * @readonly
 * @property {number} R - The radius of the curve in feet
 * @property {number} T - The length of the tangent in feet
 * @property {number} E - The length of the external distance (the distance between PI and MP)
 */
const DisplayedCurveConstants = {
  R: 881.47,
  T: 139.61,
  E: 10.99
};

/**
 * Constants for the curve, converted to meters for the simulation
 * Values are taken from the lab handout, located in the resources folder
 * @todo convert I to radians for calculations
 * @readonly
 * @property {number} R - The radius of the curve in meters
 * @property {number} E - The length of the external distance (the distance between PI and MP)
 * @property {number} T - The length of the tangent in meters
 * @property {number} I - Central angle in radians
 */
const CurveConstants = {
  R: feetToMeters(DisplayedCurveConstants.R),
  T: feetToMeters(DisplayedCurveConstants.T),
  E: feetToMeters(DisplayedCurveConstants.E),
  I: degreesToRadians(18)
};

/**
 * The map containing the layout of all the midpoint stakes
 * @typedef {Object.<string, StakePoint>} MidpointStakeLayout
 */

/**
 * Coordinates of a stake point, relative to the CT point, zeroed to the PI point
 * @typedef {Object} StakePoint
 * @property {number} DEFLECTION - The deflection angle in radians
 * @property {number} DISTANCE - The distance in meters
 */

/** @type {MidpointStakeLayout} */
const MidpointStakeLayout = {
  TP1: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(1, 16, 37)),
    DISTANCE: feetToMeters(39.29)
  },
  TP2: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(2, 54, 7)),
    DISTANCE: feetToMeters(89.25)
  },
  MP: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(4, 30, 0)),
    DISTANCE: feetToMeters(138.32)
  },
  TP3: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(4, 31, 37)),
    DISTANCE: feetToMeters(139.15)
  },
  TP4: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(6, 9, 7)),
    DISTANCE: feetToMeters(188.93)
  },
  TP5: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(7, 46, 37)),
    DISTANCE: feetToMeters(238.56)
  },
  CT2: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(9, 0, 0)),
    DISTANCE: feetToMeters(275.78)
  }
};

/**
 * Enum for all point names
 * @readonly
 * @enum {string}
 */
const PointName = {
  PI: "PI",
  TC: "TC",
  CT: "CT",
  TP1: "TP1",
  TP2: "TP2",
  TP3: "TP3",
  TP4: "TP4",
  TP5: "TP5",
  MP: "MP",
  // The second CT placement, where the error is measured
  CT2: "CT2",
  // The given location for alignment against the back tangent
  BACK_TANGENT_MARKER: "BACK_TANGENT_MARKER"
};

/**
 * Enum for step IDs
 * @readonly
 * @enum {string}
 */
const StepId = {
  ZERO_INSTRUMENT_BACKWARD: "ZERO_INSTRUMENT_BACKWARD",
  STAKE_OUT_TC: "STAKE_OUT_TC",
  STAKE_OUT_CT: "STAKE_OUT_CT",
  ZERO_INSTRUMENT_TO_PI: "ZERO_INSTRUMENT_TO_PI",
  STAKE_OUT_TP1: "STAKE_OUT_TP1",
  STAKE_OUT_TP2: "STAKE_OUT_TP2",
  STAKE_OUT_MP: "STAKE_OUT_MP",
  MEASURE_ERROR_MP: "MEASURE_ERROR_MP",
  STAKE_OUT_TP3: "STAKE_OUT_TP3",
  STAKE_OUT_TP4: "STAKE_OUT_TP4",
  STAKE_OUT_TP5: "STAKE_OUT_TP5",
  STAKE_OUT_CT2: "STAKE_OUT_CT2",
  MEASURE_ERROR_CT2: "MEASURE_ERROR_CT2"
};

/**
 * The Static content driving the steps of each section.
 * @typedef {Object} Step
 * @property {string} id - The id of the step
 * @property {string} message - The text content of the step
 * @property {string} [instructions] - The instructions for the step
 */

/**
 * The Static content driving the sections of the activity.
 * @todo: add expanded instructions for each step
 * @typedef {SectionContent}
 * @property {string} title - The title of the section
 * @property {string} [body] - The body text of the section
 * @property {string} [instructions] - Instructions for the section
 * @property {Step[]} steps - The steps of the section
 */

/** @type {SectionContent[]} */
const sections = [
  {
    title: "Setup at Point PI",
    body: `The total station has been placed at point PI.`,
    warning:
      "Click the help icon in the top right corner to view the controls for this activity.",
    showWarningFor: [StepId.ZERO_INSTRUMENT_BACKWARD, StepId.STAKE_OUT_TC],
    steps: [
      {
        id: StepId.ZERO_INSTRUMENT_BACKWARD,
        message: "Zero the Instrument against the backward tangent.",
        instructions:
          "Use the <strong>0</strong> key on the keyboard to zero the instrument"
      },
      {
        id: StepId.STAKE_OUT_TC,
        message: `Stake out the T.C. using the tangent length (T) of ${DisplayedCurveConstants.T} feet, and mark the precise location with a nail on the stake.`,
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point TC. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_CT,
        message: `Stake out the C.T. using tangent length (T) of ${DisplayedCurveConstants.T} feet and deflection angle (180°+I = 198°).<br>Then mark the precise location with a nail on the stake.`,
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point CT. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      }
    ]
  },
  {
    title: "Setup at Point TC",
    body: `The total station has been placed at point TC.`,
    // Below warning removed now that we've merged the different sections into one. Currently only one warning can be shown for any given section.
    // TODO: allow the usage of multiple warnings per section by using a map of step ids to warning messages
    // warning:
    //   'Click the <span class="badge bg-secondary">Data Table</span> button in the top right corner to view the deflection angles and distances for the different points.',
    // showWarningFor: [StepId.ZERO_INSTRUMENT_TO_PI],
    warning:
      "The original CT placement has been hidden while you determine its location from this position.",
    showWarningFor: [StepId.STAKE_OUT_CT2],
    steps: [
      {
        id: StepId.ZERO_INSTRUMENT_TO_PI,
        message: "Zero the Instrument against the backward tangent to PI.",
        instructions:
          "Use the <strong>0</strong> key on the keyboard to zero the instrument"
      },
      {
        id: StepId.STAKE_OUT_TP1,
        message: "Stake out point TP1",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point TP1. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_TP2,
        message: "Stake out point TP2",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point TP2. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_MP,
        message:
          "Stake out point MP and mark the precise location with a nail on the stake.",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point MP. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.MEASURE_ERROR_MP,
        message:
          "Measure the distance from MP to TP1 and compare it to the calculated distance.",
        instructions:
          "The measurement will be displayed in a white label between MP and TP1. If the error is greater than the acceptable error, you will need to adjust one or more of the stake points. <br><br>To navigate to a previous step, deselect the checkbox for the desired step.<br><br>The measurement will be hidden once this step is marked completed. Deselect the checkbox to see the measurement again."
      },
      {
        id: StepId.STAKE_OUT_TP3,
        message: "Stake out point TP3",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point TP3. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_TP4,
        message: "Stake out point TP4",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point TP4. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_TP5,
        message: "Stake out point TP5",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point TP5. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_CT2,
        message:
          "Re-measure the point CT from your current position, and mark the precise location with a nail.",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point CT. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.MEASURE_ERROR_CT2,
        message:
          "Measure the error distance between the original and re-staked point CT.",
        instructions:
          "The measurement will be displayed in a white label between the original and re-measured CT points. If the error is greater than the acceptable error, you will need to adjust one or more of the stake points.<br><br>To navigate to a previous step, deselect the checkbox for the desired step.<br><br>The measurement will be hidden once this step is marked completed. Deselect the checkbox to see the measurement again."
      }
    ]
  }
];

export {
  sections,
  StepId,
  PointName,
  CurveConstants,
  DisplayedCurveConstants,
  MidpointStakeLayout,
  ErrorMeasurementTolerance,
  IdealMeasurementTolerance
};
